import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'

import { Spinner } from '@/components/ui/spinner'
import { createQueryClient } from '@/lib/query-client'
import { router } from '@/routes'
import { useAuthStore } from '@/stores/auth.store'

/**
 * Application root.
 *
 * Same boot pattern as the driver PWA: hydrate auth from
 * `localStorage` first (so the route guard sees the right state
 * synchronously), then mount the QueryClient + Router.
 *
 * No offline subsystem here — the admin web is desktop-first and
 * doesn't queue work for later. If a request fails because the
 * network is gone, TanStack Query's retry handles it; the user
 * sees an error and tries again. Adding the IndexedDB queue from
 * the PWA would be premature complexity.
 */
export function App() {
  const queryClient = useMemo(createQueryClient, [])
  const hydrate = useAuthStore((s) => s.hydrate)
  const hydrated = useAuthStore((s) => s.hydrated)
  const [bootError, setBootError] = useState<string | null>(null)

  useEffect(() => {
    void hydrate().catch((err) => {
      setBootError(err instanceof Error ? err.message : String(err))
    })
  }, [hydrate])

  if (!hydrated) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-surface-muted text-text-muted">
        <Spinner size={28} label="Chargement de l'application" />
        <p className="text-sm">Chargement…</p>
        {bootError !== null ? (
          <p className="text-xs text-danger px-6 text-center">
            Erreur de démarrage : {bootError}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  )
}
